import { AnyObject } from 'antd/es/_util/type'
import { AxiosError, AxiosResponse } from 'axios'

interface FailedFields {
  [key: string]: {
    value: string
    errors: Error[]
  }
}

interface ApiFailedFields {
  [key: string]: string[]
}

type FailedFieldsForClass = { [key: string]: string[] }

type NormalizedFailedFields = {
  [key: string]: { value: unknown; errors: Error[] }
}

type NonFieldErrors = string[] | null

export class FieldsValidationError extends Error {
  private readonly failedFields: FailedFields

  constructor(responseFields: ApiFailedFields, formFields: AnyObject) {
    super('Failed fields validation')
    const failedFields: FailedFields = {}

    for (const key in responseFields) {
      if (Object.prototype.hasOwnProperty.call(responseFields, key)) {
        if (Object.prototype.hasOwnProperty.call(formFields, key)) {
          failedFields[key] = {
            value: formFields[key],
            errors: responseFields[key].map((s) => new Error(s))
          }
        } else {
          console.warn(`Missing key in form fields: ${key}`)
        }
      }
    }

    this.failedFields = failedFields
  }

  get fields(): FailedFields {
    return this.failedFields
  }
}

const triggerErrorMessage = (status?: string, content?: string) => {
  return [status, content].filter(Boolean).join(' - ')
}

export const showErrorMessage = (error: unknown) => {
  if (error instanceof AxiosError) {
    const data = error?.response?.data

    // eslint-disable-next-line no-prototype-builtins
    if (data?.hasOwnProperty('detail')) {
      return triggerErrorMessage(String(error.status), data.detail)
    } else if ('non_field_errors' in data) {
      data.non_field_errors.forEach((errorMessage: string) =>
        triggerErrorMessage(errorMessage)
      )
      return
    } else if (typeof data === 'object') {
      const errors = (data: AxiosResponse['data']) =>
        new AxiosError(error.message, error.code, error.config, error.request, {
          ...(error.response as AxiosResponse),
          data
        })
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (typeof value[0] === 'string') {
            triggerErrorMessage(
              `${key} - ${value.reduce((prev, next) => prev + ', ' + next)}`
            )
          } else {
            showErrorMessage(errors(value))
          }
        } else {
          showErrorMessage(errors(value))
        }
      })
      return
    }
    return triggerErrorMessage(error.message)
  }
}

function buildNormalizedFields(
  formFields: { [key: string]: unknown },
  failedFields: FailedFieldsForClass
): NormalizedFailedFields {
  const normalizedFields: NormalizedFailedFields = {}
  const fields = new Set<string>(
    Object.keys(failedFields).concat(Object.keys(formFields))
  )

  for (const key of fields) {
    normalizedFields[key] = {
      value: undefined,
      errors: []
    }

    if (Object.prototype.hasOwnProperty.call(formFields, key)) {
      normalizedFields[key].value = formFields[key]
    }

    if (
      Object.prototype.hasOwnProperty.call(failedFields, key) &&
      Array.isArray(failedFields[key])
    ) {
      normalizedFields[key].errors = failedFields[key].map((s) => new Error(s))
    }
  }

  return normalizedFields
}

export class ValidationError extends Error {
  readonly messages: string[]
  readonly fields: NormalizedFailedFields
  readonly response: FailedFieldsForClass

  constructor(
    body: { [key: string]: unknown },
    data: FailedFieldsForClass,
    nonFields: NonFieldErrors = null
  ) {
    super('Failed validation')
    this.fields = buildNormalizedFields(body, data)
    this.response = data

    if (nonFields === null) {
      this.messages = []
    } else {
      this.messages = nonFields
    }
  }
}
